import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Input, InputEvent } from '@/components/form/Input';
import { Slider } from '@/components/Slider';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RATE_LIMIT_FIXED_VALUES } from '@/config/rateLimitConfig';
import { useAxios } from '@/hooks/useAxios';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { useUpdateSetting } from '@/hooks/useSetting';
import { AssignProfileToAssetsContent } from '@/sections/settings/components/modals/AssignProfileToAssetsModal';
import { DeleteConfirmRow } from '@/sections/settings/components/primitives/DeleteConfirmRow';
import { DeleteConfirmRowTyped } from '@/sections/settings/components/primitives/DeleteConfirmRowTyped';
import type { ScanProfileFooterState } from '@/sections/settings/components/primitives/scanProfileFooterState';
import { formatInheritLabel } from '@/sections/settings/utils/formatInherit';
import { ScanProfile } from '@/types/scanProfile';
import { flattenAssetGraphResponse } from '@/utils/api/flattenAssetGraphResponse';

const INHERIT_SENTINEL = 999999; // out-of-band slider value meaning "no override; inherit globals"
const HARD_DELETE_CAP = 200; // max assigned assets for which cascade delete is permitted

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export type SingleScanProfileFormProps = {
  customerEmail?: string;
  globalRateLimit: { simultaneousHosts: number; capabilityRateLimit: number };
  onClose?: () => void;
  onFooterStateChange?: (state: ScanProfileFooterState) => void;
} & (
  | { mode: 'create'; profile?: never }
  | { mode: 'edit'; profile: ScanProfile }
);

export const SingleScanProfileForm: React.FC<SingleScanProfileFormProps> = ({
  customerEmail,
  globalRateLimit,
  mode,
  profile,
  onClose,
  onFooterStateChange,
}) => {
  const { profiles: apiProfiles } = useScanProfiles(customerEmail);
  const { mutateAsync: updateSetting, status: updateStatus } =
    useUpdateSetting(customerEmail);
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { canWriteAssets } = useEntitlements();

  // Wizard step state
  const [step, setStep] = useState<1 | 2>(1);
  const [savedProfile, setSavedProfile] = useState<ScanProfile | null>(null);

  // Form state
  const [name, setName] = useState<string>(
    mode === 'edit' && profile ? profile.name : ''
  );
  const [simultaneousHosts, setSimultaneousHosts] = useState<number>(
    mode === 'edit' && profile && profile.simultaneousHosts !== undefined
      ? profile.simultaneousHosts
      : INHERIT_SENTINEL
  );
  const [capabilityRateLimit, setCapabilityRateLimit] = useState<number>(
    mode === 'edit' && profile && profile.capabilityRateLimit !== undefined
      ? profile.capabilityRateLimit
      : INHERIT_SENTINEL
  );

  // Inline error state for name uniqueness
  const [nameError, setNameError] = useState<string>('');

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // Count of assets currently assigned to this profile — fetched lazily when
  // user clicks Delete. Used to choose between plain confirm vs typed confirm
  // (B2.3) and to enforce the hard-cap guard (B2.2).
  const [assignedAssetsCount, setAssignedAssetsCount] = useState<number | null>(
    null
  );
  // Separate loading state for the delete cascade so the "Yes, delete" button
  // is disabled while the async unassignment + setting update is in flight.
  const [isDeleting, setIsDeleting] = useState(false);

  const isLoading = updateStatus === 'pending';

  const handleNameChange = (e: InputEvent) => {
    const val = e.target.value;
    setName(val);
    setNameError('');
  };

  const validateName = useCallback((): boolean => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Profile name is required.');
      return false;
    }
    const slug = slugify(trimmed);
    if (!slug) {
      setNameError('Profile name must contain at least one letter or number.');
      return false;
    }
    // Uniqueness check: exclude self when editing
    const otherProfiles =
      mode === 'edit' && profile
        ? apiProfiles.filter(p => p.id !== profile.id)
        : apiProfiles;
    const isDuplicate = otherProfiles.some(
      p => p.id === slug || p.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      setNameError('A profile with this name already exists.');
      return false;
    }
    return true;
  }, [name, mode, profile, apiProfiles]);

  const handleNext = useCallback(async () => {
    if (!validateName()) return;

    try {
      const trimmed = name.trim();
      const id = mode === 'edit' ? profile.id : slugify(trimmed);

      const newProfile: ScanProfile = {
        id,
        name: trimmed,
        ...(simultaneousHosts !== INHERIT_SENTINEL && { simultaneousHosts }),
        ...(capabilityRateLimit !== INHERIT_SENTINEL && {
          capabilityRateLimit,
        }),
      };

      let updatedProfiles: ScanProfile[];
      if (mode === 'create') {
        updatedProfiles = [...apiProfiles, newProfile];
      } else {
        // Replace existing profile (match by original id)
        // mode === 'edit' guarantees profile is defined via discriminated union
        updatedProfiles = apiProfiles.map(p =>
          p.id === profile.id ? newProfile : p
        );
      }

      await updateSetting({
        name: 'scan-profiles',
        value: updatedProfiles,
        silent: true,
      });

      // Move to step 2 — do NOT show toast here; confirmation comes when user
      // finishes step 2 (Save/Skip).
      setSavedProfile(newProfile);
      setStep(2);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 409) {
        toast.error('Scan profiles changed concurrently', {
          description:
            'Reload and retry — another tab or user updated this setting.',
        });
        return;
      }
      toast.error('Failed to save override', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  }, [
    validateName,
    name,
    mode,
    profile,
    simultaneousHosts,
    capabilityRateLimit,
    apiProfiles,
    updateSetting,
  ]);

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) {
      // First click: fetch assigned-asset count to gate both the hard-cap check
      // (B2.2) and the typed-confirm decision (B2.3). Fetching HARD_DELETE_CAP+1
      // lets us detect overflow without fetching the full list.
      setIsDeleting(true);
      try {
        const { data } = await axios.post(
          '/my',
          {
            node: {
              labels: ['Asset'],
              filters: [
                { field: 'profile', operator: '=', value: profile!.id },
              ],
            },
            limit: HARD_DELETE_CAP + 1,
          },
          { params: { label: 'asset' } }
        );
        const previewAssets = flattenAssetGraphResponse(data);

        // B2.2 hard-cap guard: if assigned > cap, block the delete with an error.
        if (previewAssets.length > HARD_DELETE_CAP) {
          toast.error(
            `This profile is assigned to more than ${HARD_DELETE_CAP} assets. ` +
              `Bulk delete is unavailable; please contact support to remove the override safely.`
          );
          setIsDeleting(false);
          setDeleteConfirm(false);
          return;
        }

        // Stash count so the confirm row knows whether to require typed input.
        setAssignedAssetsCount(previewAssets.length);
        setDeleteConfirm(true);
      } catch (e) {
        console.error('handleDelete pre-fetch error', e);
        toast.error('Failed to check assigned assets', {
          description: e instanceof Error ? e.message : 'Unknown error',
        });
      } finally {
        setIsDeleting(false);
      }
      return;
    }

    // Confirmed click: run the cascade.
    // handleDelete is only reachable in edit mode; profile is always defined here.
    setIsDeleting(true);
    try {
      // 1. Fetch assets currently using this profile (full list, up to cap).
      //    We re-fetch here instead of reusing the preview list because the user
      //    may have taken time to type the confirmation and new assets could have
      //    been assigned in the interim. Cap matches HARD_DELETE_CAP (already
      //    verified above), so no silent-skip risk.
      const { data } = await axios.post(
        '/my',
        {
          node: {
            labels: ['Asset'],
            filters: [{ field: 'profile', operator: '=', value: profile!.id }],
          },
          limit: HARD_DELETE_CAP,
        },
        { params: { label: 'asset' } }
      );

      const assignedAssets = flattenAssetGraphResponse(data);

      // Cascade is server-side: PUT /setting {scan-profiles} triggers a bulk-action
      // job that clears the profile from every assigned asset. See backend commit
      // 6c5e09d8a. Frontend just removes the profile from the array and trusts the
      // server. Client-side runChunked PUTs were broken (MergeFields skips empty
      // Profile) — the bulk worker uses aws.Graph.Insert to bypass merge.
      const updatedProfiles = apiProfiles.filter(p => p.id !== profile!.id);
      await updateSetting({
        name: 'scan-profiles',
        value: updatedProfiles,
        silent: true,
      });

      // Invalidate asset cache so any open list eventually reflects the cleared
      // assignments. The server-side bulk job runs asynchronously; cache
      // invalidation now reflects the queued cleanup, and useActiveBulkJobs will
      // re-invalidate when the job terminates.
      queryClient.invalidateQueries({
        predicate: q => {
          const qk = q.queryKey as string[];
          return qk[0] === 'MY' && qk[1] === 'asset';
        },
      });

      // Toast — server cascades async; report what's been queued.
      if (assignedAssets.length > 0) {
        toast.success(
          `Override deleted. Cleanup queued for ${assignedAssets.length} asset${assignedAssets.length === 1 ? '' : 's'} — track in bulk actions panel.`
        );
      } else {
        toast.success('Override deleted');
      }

      onClose?.();
    } catch (e) {
      console.error('handleDelete cascade error', e);
      if (isAxiosError(e) && e.response?.status === 409) {
        toast.error('Scan profiles changed concurrently', {
          description:
            'Reload and retry — another tab or user updated this setting.',
        });
        return;
      }
      toast.error('Failed to delete override', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteConfirm,
    profile,
    axios,
    apiProfiles,
    updateSetting,
    queryClient,
    onClose,
  ]);

  // Refs deliberately replace `handleNext`, `handleDelete`, AND
  // `onFooterStateChange` in the report-up effect deps. Putting any of these
  // in the dep array directly causes a "Maximum update depth exceeded" loop:
  // - `handleNext`/`handleDelete` change identity because their useCallback
  //   deps include TanStack-Query-derived arrays (apiProfiles) that are a
  //   fresh reference each render.
  // - `onFooterStateChange` is often passed as an inline arrow by consumers
  //   (e.g. `(state) => { capture(state); setFooter(state); }`), which
  //   recreates each render. Reading via ref breaks both cycles.
  // See test 'report-up effect does not loop' for regression coverage.
  const handleNextRef = useRef(handleNext);
  handleNextRef.current = handleNext;
  const handleDeleteRef = useRef(handleDelete);
  handleDeleteRef.current = handleDelete;
  const onFooterStateChangeRef = useRef(onFooterStateChange);
  onFooterStateChangeRef.current = onFooterStateChange;

  // Report step-1 footer state up via onFooterStateChange
  useEffect(() => {
    if (step !== 1) return;

    const leftSlot =
      mode === 'edit' ? (
        deleteConfirm ? (
          // B2.3: if the profile has assigned assets, require typed confirmation.
          assignedAssetsCount !== null && assignedAssetsCount > 0 ? (
            <DeleteConfirmRowTyped
              expected={profile!.name}
              onConfirm={() => handleDeleteRef.current()}
              onCancel={() => {
                setDeleteConfirm(false);
                setAssignedAssetsCount(null);
              }}
              disabled={isLoading || isDeleting}
              isDeleting={isDeleting}
            />
          ) : (
            <DeleteConfirmRow
              onConfirm={() => handleDeleteRef.current()}
              onCancel={() => {
                setDeleteConfirm(false);
                setAssignedAssetsCount(null);
              }}
              disabled={isLoading || isDeleting}
              isDeleting={isDeleting}
            />
          )
        ) : (
          (() => {
            const blockedByEntitlement = !canWriteAssets;
            const button = (
              <Button
                variant="ghost"
                onClick={() => handleDeleteRef.current()}
                disabled={isLoading || isDeleting || blockedByEntitlement}
                data-testid="single-scan-profile-delete-button"
              >
                {isDeleting ? 'Checking...' : 'Delete'}
              </Button>
            );
            if (!blockedByEntitlement) return button;
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>{button}</span>
                </TooltipTrigger>
                <TooltipContent>
                  Removing this profile cascades to assets. You need WriteAssets
                  in addition to ManageSettings.
                </TooltipContent>
              </Tooltip>
            );
          })()
        )
      ) : undefined;

    onFooterStateChangeRef.current?.({
      primary: {
        label: 'Next',
        onClick: () => handleNextRef.current(),
        disabled: isLoading,
        'data-testid': 'single-scan-profile-save-button',
      },
      left: leftSlot,
    });
  }, [
    step,
    mode,
    isLoading,
    isDeleting,
    deleteConfirm,
    assignedAssetsCount,
    canWriteAssets,
    profile,
  ]);

  // ── Step 2: Asset assignment ──────────────────────────────────────────────
  if (step === 2 && savedProfile) {
    return (
      <AssignProfileToAssetsContent
        open={true}
        profile={savedProfile}
        mode={mode === 'edit' ? 'manage' : 'create'}
        onClose={onClose ?? (() => {})}
        onSkip={onClose}
        onBack={() => setStep(1)}
        onFooterStateChange={onFooterStateChange}
      />
    );
  }

  // ── Step 1: Profile settings ──────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-8" data-testid="single-scan-profile-form">
      {/* Name field */}
      <div className="flex flex-col gap-1">
        <Input
          name="profile-name"
          label="Profile Name"
          value={name}
          onChange={handleNameChange}
          placeholder="e.g., Sensitive Sites"
          disabled={isLoading}
          data-testid="single-scan-profile-name-input"
        />
        {nameError && (
          <p
            className="text-chariot-status-critical text-xs"
            data-testid="single-scan-profile-name-error"
          >
            {nameError}
          </p>
        )}
      </div>

      {/* Sliders */}
      <div className="space-y-8">
        <div className="border-chariot-stroke border-b pb-8">
          <Slider
            min={1}
            max={RATE_LIMIT_FIXED_VALUES.FULL_RATE_SIMULTANEOUS_HOSTS}
            value={simultaneousHosts}
            onChange={setSimultaneousHosts}
            enableUnlimited={true}
            unlimitedLabel={formatInheritLabel(
              globalRateLimit.simultaneousHosts
            )}
            inheritedMarker={globalRateLimit.simultaneousHosts}
            title="Maximum simultaneous hosts scanned"
            subtitle="Controls how many assets with this profile are scanned in parallel."
            data-testid="single-scan-profile-hosts-slider"
          />
        </div>
        <div>
          <Slider
            min={1}
            max={RATE_LIMIT_FIXED_VALUES.FULL_RATE_REQUESTS_PER_HOST}
            value={capabilityRateLimit}
            onChange={setCapabilityRateLimit}
            enableUnlimited={true}
            unlimitedLabel={formatInheritLabel(
              globalRateLimit.capabilityRateLimit,
              'rq/s'
            )}
            inheritedMarker={globalRateLimit.capabilityRateLimit}
            title="Maximum rate of requests per host"
            subtitle="Controls scanning intensity per individual asset with this profile."
            unit="rq/s"
            data-testid="single-scan-profile-rate-slider"
          />
        </div>
      </div>
    </div>
  );
};
